import React, { useState, useEffect } from "react";
import { Button } from "components";
import { CompanyHRObjectivesData } from "../types";
import { useForm } from "react-hook-form";

interface CompanyHRObjectivesProps {
  submit: (data: CompanyHRObjectivesData) => void;
  init: CompanyHRObjectivesData;
  goBack: () => void;
}

const usageOptions = [
  { label: "Hire for open roles", value: "hire_open_roles" },
  { label: "Benchmark leadership", value: "benchmark_leadership" },
  { label: "Role fit (promotion/transfer)", value: "role_fit" },
  { label: "Workforce gap analysis", value: "workforce_gap" },
];

const cultureOptions = [
  { label: "Work Independently", value: "work_independently" },
  { label: "Collaborate Effectively", value: "collaborate_effectively" },
  { label: "Pursue Excellence", value: "pursue_excellence" },
  { label: "Act as Brand Ambassadors", value: "brand_ambassadors" },
  { label: "Demonstrate Passion", value: "demonstrate_passion" },
  { label: "Exhibit Charisma in Leadership", value: "exhibit_charisma" },
  { label: "Drive Financial Success", value: "drive_financial_success" },
];

const roleTypeOptions = [
  { label: "Operational Roles", value: "operational_roles" },
  { label: "Technical Roles", value: "technical_roles" },
  { label: "Creative Roles", value: "creative_roles" },
  { label: "Business Roles", value: "business_roles" },
  { label: "Leadership Roles", value: "leadership_roles" },
  { label: "Others", value: "others" },
];

export const CompanyHRObjectivesForm: React.FC<CompanyHRObjectivesProps> = ({
  submit,
  init,
  goBack,
}) => {
  // Ensure init has all required fields with default values
  const [formData, setFormData] = useState<CompanyHRObjectivesData>({
    usage: init?.usage || [],
    culture: init?.culture || [],
    roleTypes: init?.roleTypes || [],
    hrManagement: init?.hrManagement || "",
    teamCultureValues: init?.teamCultureValues || [],
    assessmentTypes: init?.assessmentTypes || [],
    hiringChallenges: init?.hiringChallenges || [],
  });

  // Sync form state when init changes (e.g., when navigating back from step 3)
  useEffect(() => {
    if (init) {
      setFormData({
        usage: init.usage || [],
        culture: init.culture || [],
        roleTypes: init.roleTypes || [],
        hrManagement: init.hrManagement || "",
        teamCultureValues: init.teamCultureValues || [],
        assessmentTypes: init.assessmentTypes || [],
        hiringChallenges: init.hiringChallenges || [],
      });
    }
  }, [init]);

  const {
    register,
    formState: { errors },
  } = useForm();

  const handleMultiSelect = (category: keyof CompanyHRObjectivesData, value: string) => {
    if (category === 'hrManagement') {
      setFormData(prev => ({ ...prev, [category]: value }));
      return;
    }

    // Ensure currentValues is an array, default to [] if undefined
    const currentValues = Array.isArray(formData[category]) ? formData[category] as string[] : [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value];

    setFormData(prev => ({ ...prev, [category]: newValues }));
  };

  const handleSubmit = () => {
    submit(formData);
  };

  return (
    <div className="max-h-[380px] overflow-y-auto scrollbar-none">
      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          How Will You Use Precision?
        </label>
        <div className="flex flex-wrap gap-2">
          {usageOptions?.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('usage', option?.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm hover:bg-white font-medium transition-colors ${
                formData?.usage?.includes(option.value)
                  ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF] hover:bg-gray-200'
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Which values best describe your team's culture?
        </label>
        <div className="flex flex-wrap gap-3">
          {cultureOptions?.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('culture', option.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm font-medium hover:bg-white transition-colors ${
                formData?.culture?.includes(option.value)
                  ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]'
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Which types of roles do you most often assess with Precision?
        </label>
        <div className="flex flex-wrap gap-3">
          {roleTypeOptions?.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('roleTypes', option.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm font-medium hover:bg-white transition-colors ${
                formData?.roleTypes?.includes(option.value)
                  ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]'
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          className="max-w-sm flex-1 border !border-[#012169] !text-[#012169] bg-white"
          type="outline"
          onClick={goBack}
        >
          Back
        </Button>
        <Button
          className="max-w-sm flex-1 !bg-[#012169] !border-none text-white"
          type="fill"
          onClick={handleSubmit}
        >
          Next
        </Button>
      </div>
    </div>
  );
};